import { syncNow } from "./sync";

export type PwaWorkerStage = "unsupported" | "idle" | "registering" | "ready" | "error";

export interface PwaWorkerState {
  stage: PwaWorkerStage;
  controlled: boolean;
  error: string | null;
}

declare global {
  interface Window {
    __trackerAssetsCached?: Promise<void>;
  }
}

export const PWA_STATUS_EVENT = "tracker-pwa-status";
let pwaWorkerState: PwaWorkerState = {
  stage: "serviceWorker" in navigator ? "idle" : "unsupported",
  controlled: Boolean(navigator.serviceWorker?.controller),
  error: null
};

export function getPwaWorkerState(): PwaWorkerState {
  return { ...pwaWorkerState };
}

export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  const register = () => {
    updatePwaWorkerState({ stage: "registering", error: null });
    window.__trackerAssetsCached = navigator.serviceWorker
      .register("/sw.js")
      .then((registration) => navigator.serviceWorker.ready.then(() => registration))
      .then(async (registration) => {
        await cacheLoadedAssets(registration);
        updatePwaWorkerState({
          stage: "ready",
          controlled: Boolean(navigator.serviceWorker.controller),
          error: null
        });
      })
      .catch((error: unknown) => {
        updatePwaWorkerState({
          stage: "error",
          controlled: false,
          error: error instanceof Error ? error.message : String(error)
        });
      });
  };

  if (document.readyState === "complete") {
    register();
  } else {
    window.addEventListener("load", register, { once: true });
  }

  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type === "tracker-sync-request") {
      syncNow().catch(() => undefined);
    }
  });
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    updatePwaWorkerState({
      controlled: Boolean(navigator.serviceWorker.controller)
    });
  });
}

async function cacheLoadedAssets(registration: ServiceWorkerRegistration): Promise<void> {
  const worker = registration.active ?? navigator.serviceWorker.controller;
  if (!worker) {
    return;
  }
  const urls = loadedSameOriginUrls();
  if (!urls.length) {
    return;
  }
  await postMessageWithAck(worker, { type: "tracker-cache-urls", urls });
}

function loadedSameOriginUrls(): string[] {
  const urls = new Set<string>(["/", "/index.html", "/manifest.webmanifest", "/icons/icon.svg"]);
  for (const entry of performance.getEntriesByType("resource")) {
    const url = new URL(entry.name);
    if (url.origin === location.origin && !url.pathname.startsWith("/api/")) {
      urls.add(url.pathname + url.search);
    }
  }
  return [...urls];
}

function postMessageWithAck(worker: ServiceWorker, message: unknown): Promise<void> {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timeout = window.setTimeout(resolve, 2_000);
    channel.port1.onmessage = () => {
      window.clearTimeout(timeout);
      resolve();
    };
    worker.postMessage(message, [channel.port2]);
  });
}

function updatePwaWorkerState(updates: Partial<PwaWorkerState>): void {
  pwaWorkerState = { ...pwaWorkerState, ...updates };
  window.dispatchEvent(new CustomEvent(PWA_STATUS_EVENT, { detail: getPwaWorkerState() }));
}
